import { resolveBasePath } from "../helpers/basePath";
import type { SupportedLocale } from "./locales";

const MAX_FLATTEN_DEPTH = 20;

/**
 * Loads translations from public/locales/{locale}/common.json.
 * This approach allows translations to be updated without rebuilding.
 */
export async function loadTranslations(locale: SupportedLocale): Promise<Record<string, Record<string, string>>> {
  return await loadTranslationFile(locale, "common.json");
}

/**
 * Loads a module translation file from public/locales/{locale}/{module}.module.json.
 */
export async function loadModuleTranslations(locale: SupportedLocale, moduleName: string): Promise<Record<string, Record<string, string>>> {
  return await loadTranslationFile(locale, `${moduleName}.module.json`);
}

async function loadTranslationFile(locale: SupportedLocale, fileName: string): Promise<Record<string, Record<string, string>>> {
  const response = await fetch(`${resolveBasePath()}/locales/${locale}/${fileName}`);
  if (!response.ok) {
    throw new Error(`Failed to load translation file '${fileName}' for locale: ${locale}`);
  }

  const data = (await response.json()) as Record<string, unknown>;
  return { [locale]: flattenObject(data) };
}

/**
 * Flattens a nested object into dot-notation keys.
 * Example: { navigation: { admin: "Admin" } } => { "navigation.admin": "Admin" }
 */
function flattenObject(obj: Record<string, unknown>, prefix = "", depth = 0, visited = new WeakSet<object>()): Record<string, string> {
  if (depth > MAX_FLATTEN_DEPTH) {
    throw new Error(`Maximum translation nesting depth (${MAX_FLATTEN_DEPTH}) exceeded at '${prefix || "<root>"}'`);
  }

  if (visited.has(obj)) {
    throw new Error(`Circular translation structure detected at '${prefix || "<root>"}'`);
  }

  visited.add(obj);
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, newKey, depth + 1, visited));
    } else if (typeof value === "string") {
      result[newKey] = value;
    }
  }

  visited.delete(obj);

  return result;
}
